import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './OperatorQuotations.css';
import { formatCurrency } from '../utils/formatters';
import CreateQuotationPage from './CreateQuotationPage';

const API = '/api/quotations';
const CLIENTS_API = '/api/clients';
const ROUTES_API = '/api/routes';
const UPLOAD_URL = '/api/uploads/';

/* ── Inline SVG Icons (Lucide-style) ── */
const Icon = ({ d, size = 16, strokeWidth = 2, color = 'currentColor', style = {} }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0, ...style }}
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: ['M12 5v14', 'M5 12h14'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    file: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6'],
    eye: ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
    check: 'M20 6L9 17l-5-5',
    checkCircle: ['M22 11.08V12a10 10 0 1 1-5.93-9.14', 'M22 4L12 14.01l-3-3'],
    clock: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 6v6l4 2'],
    send: ['M22 2L11 13', 'M22 2l-7 20-4-9-9-4 20-7z'],
    xCircle: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M15 9l-6 6', 'M9 9l6 6'],
    trending: ['M23 6l-9.5 9.5-5-5L1 18', 'M17 6h6v6'],
    dollar: ['M12 2v20', 'M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'],
    refresh: ['M23 4v6h-6', 'M1 20v-6h6', 'M3.51 9a9 9 0 0 1 14.85-3.36L23 10', 'M1 14l4.64 4.36A9 9 0 0 0 20.49 15'],
    download: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3'],
    printer: ['M6 9V2h12v7', 'M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2', 'M6 14h12v8H6z'],
    x: ['M18 6L6 18', 'M6 6l12 12'],
    building: ['M3 21h18', 'M3 7v14m18-14v14', 'M3 7l9-4 9 4', 'M5 21V11m4 10V11m6 10V11m4 10V11'],
    home: ['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M9 22V12h6v10'],
};

const STATUS_CONFIG = {
    draft: { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1', dot: '#94a3b8', label: 'Draft' },
    sent: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', dot: '#3b82f6', label: 'Sent' },
    accepted: { bg: '#ecfdf5', color: '#059669', border: '#a7f3d0', dot: '#10b981', label: 'Accepted' },
    rejected: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca', dot: '#ef4444', label: 'Declined' },
    expired: { bg: '#fffbeb', color: '#d97706', border: '#fde68a', dot: '#f59e0b', label: 'Expired' },
};

export default function OperatorQuotations({ user: propUser, operatorDetails }) {
    const [quotations, setQuotations] = useState([]);
    const [clients, setClients] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [clientFilter, setClientFilter] = useState('');
    const [createPageOpen, setCreatePageOpen] = useState(false);
    const [editItemForPage, setEditItemForPage] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedQuotation, setSelectedQuotation] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [toast, setToast] = useState(null);

    const user = propUser || JSON.parse(localStorage.getItem('ops-user'));
    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : 'all');
    const country = operatorDetails?.country;

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    useEffect(() => {
        if (operatorId) fetchData();
    }, [operatorId]);

    const fetchData = async () => {
        if (!operatorId) return;
        setLoading(true);
        try {
            const [qRes, clientRes, routeRes] = await Promise.all([
                axios.get(`${API}/${operatorId}`).catch(() => ({ data: [] })),
                axios.get(`${CLIENTS_API}/${operatorId}`).catch(() => ({ data: [] })),
                axios.get(`${ROUTES_API}/${operatorId}`).catch(() => ({ data: [] }))
            ]);
            setQuotations(Array.isArray(qRes.data) ? qRes.data : []);
            setClients(Array.isArray(clientRes.data) ? clientRes.data : []);
            setRoutes(Array.isArray(routeRes.data) ? routeRes.data : []);
        } catch (err) {
            console.error('Quotations fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (item = null) => {
        setEditItemForPage(item || null);
        setCreatePageOpen(true);
    };

    const handleViewQuotation = (q) => {
        setSelectedQuotation(q);
        setViewModalOpen(true);
    };

    const handleDelete = async (id, qNum) => {
        if (!window.confirm(`Are you sure you want to delete quotation "${qNum || id}"?`)) return;
        try {
            await axios.delete(`${API}/${id}`);
            showToast('Quotation deleted successfully');
            fetchData();
        } catch (err) {
            showToast('Failed to delete quotation', 'error');
        }
    };

    const handlePrint = () => {
        const content = document.getElementById('printable-quotation');
        if (!content) return;
        const printWindow = window.open('', '', 'width=900,height=700');
        printWindow.document.write('<html><head><title>Print Quotation</title>');
        printWindow.document.write('<style>body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 30px; } table { width: 100%; border-collapse: collapse; margin-top: 20px; } th, td { border: 1px solid #e2e8f0; padding: 10px; text-align: left; } th { background: #f8fafc; font-size: 12px; } .badge { display: inline-block; padding: 3px 8px; border-radius: 6px; font-size: 11px; font-weight: bold; }</style>');
        printWindow.document.write('</head><body>');
        printWindow.document.write(content.innerHTML);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => { printWindow.print(); printWindow.close(); }, 300);
    };

    const exportToCSV = () => {
        if (!quotations.length) {
            showToast('No quotations to export', 'error');
            return;
        }
        const headers = ['Quotation #', 'Customer', 'Date', 'Valid Until', 'Subject', 'Status', 'Total Amount'];
        const rows = filtered.map(q => [
            q.quotationNumber || '',
            q.clientId?.companyName || 'Unknown',
            q.issueDate ? q.issueDate.split('T')[0] : '',
            q.validUntil ? q.validUntil.split('T')[0] : '',
            `"${(q.subject || '').replace(/"/g, '""')}"`,
            q.status || 'Draft',
            q.totalAmount || 0
        ]);

        const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `Quotations_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast('Exported CSV successfully');
    };

    /* ── KPI Calculations ── */
    const sentCount = quotations.filter(q => q.status?.toLowerCase() === 'sent').length;
    const acceptedCount = quotations.filter(q => q.status?.toLowerCase() === 'accepted').length;
    const declinedCount = quotations.filter(q => q.status?.toLowerCase() === 'rejected').length;
    const draftCount = quotations.filter(q => !q.status || q.status?.toLowerCase() === 'draft').length;
    const totalPipelineValue = quotations.reduce((acc, q) => acc + (q.totalAmount || 0), 0);
    const acceptedValue = quotations.filter(q => q.status?.toLowerCase() === 'accepted').reduce((acc, q) => acc + (q.totalAmount || 0), 0);

    /* ── Filtered list ── */
    const filtered = useMemo(() => {
        return quotations.filter(q => {
            const term = searchTerm.toLowerCase();
            const matchSearch = !searchTerm ||
                q.quotationNumber?.toLowerCase().includes(term) ||
                q.clientId?.companyName?.toLowerCase().includes(term) ||
                q.subject?.toLowerCase().includes(term);
            const matchStatus = !statusFilter || q.status?.toLowerCase() === statusFilter.toLowerCase();
            const matchClient = !clientFilter || q.clientId?._id === clientFilter;
            return matchSearch && matchStatus && matchClient;
        });
    }, [quotations, searchTerm, statusFilter, clientFilter]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const paginatedData = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, page, pageSize]);

    if (createPageOpen) {
        return (
            <div className="operator-page" style={{ padding: 0 }}>
                <CreateQuotationPage
                    user={propUser}
                    operatorDetails={operatorDetails}
                    editItem={editItemForPage}
                    onBack={() => setCreatePageOpen(false)}
                    onSaved={() => { setCreatePageOpen(false); fetchData(); }}
                />
            </div>
        );
    }

    return (
        <div className="op-quotations-container">
            {toast && (
                <div className={`op-toast ${toast.type}`}>
                    <Icon d={toast.type === 'error' ? icons.xCircle : icons.checkCircle} size={18} color="#fff" />
                    <span>{toast.msg}</span>
                </div>
            )}

            {/* Breadcrumbs */}
            <div className="op-breadcrumb">
                <span className="home-icon-svg"><Icon d={icons.home} size={15} color="#64748b" /></span>
                <span className="sep">›</span>
                <span>Finance</span>
                <span className="sep">›</span>
                <span className="current">Quotations</span>
            </div>

            {/* Page Header */}
            <div className="op-quotations-header">
                <div className="op-header-info">
                    <h1 className="op-page-title">Quotation Management</h1>
                    <p className="op-page-subtitle">Create, monitor, and deliver customized pricing proposals for clients</p>
                </div>
                <div className="op-header-actions">
                    <button className="op-btn-primary" onClick={() => handleOpenModal()}>
                        <Icon d={icons.plus} size={18} strokeWidth={2.5} color="#fff" />
                        <span>Create Quotation</span>
                    </button>
                </div>
            </div>

            {/* KPI Stat Cards */}
            <div className="op-stats-grid">
                <div className="op-stat-card">
                    <div className="stat-card-inner">
                        <div>
                            <span className="stat-label">Total Pipeline</span>
                            <div className="stat-number">{formatCurrency(totalPipelineValue, country)}</div>
                            <span className="stat-sub">{quotations.length} total proposal{quotations.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="stat-icon-badge badge-blue">
                            <Icon d={icons.dollar} size={20} color="#16539C" strokeWidth={2.2} />
                        </div>
                    </div>
                </div>

                <div className="op-stat-card">
                    <div className="stat-card-inner">
                        <div>
                            <span className="stat-label">Accepted</span>
                            <div className="stat-number">{acceptedCount}</div>
                            <span className="stat-sub">{formatCurrency(acceptedValue, country)} revenue</span>
                        </div>
                        <div className="stat-icon-badge badge-emerald">
                            <Icon d={icons.checkCircle} size={20} color="#059669" strokeWidth={2.2} />
                        </div>
                    </div>
                </div>

                <div className="op-stat-card">
                    <div className="stat-card-inner">
                        <div>
                            <span className="stat-label">Sent Proposals</span>
                            <div className="stat-number">{sentCount}</div>
                            <span className="stat-sub">Awaiting decision</span>
                        </div>
                        <div className="stat-icon-badge badge-indigo">
                            <Icon d={icons.send} size={20} color="#00B2A9" strokeWidth={2.2} />
                        </div>
                    </div>
                </div>

                <div className="op-stat-card">
                    <div className="stat-card-inner">
                        <div>
                            <span className="stat-label">Drafts</span>
                            <div className="stat-number">{draftCount}</div>
                            <span className="stat-sub">Work in progress</span>
                        </div>
                        <div className="stat-icon-badge badge-slate">
                            <Icon d={icons.file} size={20} color="#64748b" strokeWidth={2.2} />
                        </div>
                    </div>
                </div>

                <div className="op-stat-card">
                    <div className="stat-card-inner">
                        <div>
                            <span className="stat-label">Declined</span>
                            <div className="stat-number">{declinedCount}</div>
                            <span className="stat-sub">Unsuccessful</span>
                        </div>
                        <div className="stat-icon-badge badge-rose">
                            <Icon d={icons.xCircle} size={20} color="#dc2626" strokeWidth={2.2} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Card with Toolbar and Table */}
            <div className="op-table-card">
                {/* Modern Toolbar */}
                <div className="op-table-toolbar">
                    <div className="toolbar-left">
                        {/* Search Input */}
                        <div className="op-search-wrap">
                            <Icon d={icons.search} size={17} color="#94a3b8" />
                            <input
                                type="text"
                                className="op-search-input"
                                placeholder="Search by quotation #, customer, or subject..."
                                value={searchTerm}
                                onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                            />
                            {searchTerm && (
                                <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                                    <Icon d={icons.x} size={14} color="#94a3b8" />
                                </button>
                            )}
                        </div>

                        {/* Status Filter */}
                        <div className="op-select-wrap">
                            <select
                                className="op-toolbar-select"
                                value={statusFilter}
                                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                            >
                                <option value="">All Statuses</option>
                                <option value="Draft">Draft</option>
                                <option value="Sent">Sent</option>
                                <option value="Accepted">Accepted</option>
                                <option value="Rejected">Declined</option>
                                <option value="Expired">Expired</option>
                            </select>
                        </div>

                        {/* Client Filter */}
                        {clients.length > 0 && (
                            <div className="op-select-wrap">
                                <select
                                    className="op-toolbar-select"
                                    value={clientFilter}
                                    onChange={e => { setClientFilter(e.target.value); setPage(1); }}
                                >
                                    <option value="">All Clients</option>
                                    {clients.map(c => (
                                        <option key={c._id} value={c._id}>{c.companyName}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    <div className="toolbar-right">
                        <button className="op-btn-secondary" onClick={exportToCSV} title="Export current list to CSV">
                            <Icon d={icons.download} size={15} color="#475569" />
                            <span>Export CSV</span>
                        </button>
                        <button className="op-btn-icon" onClick={fetchData} title="Refresh records">
                            <Icon d={icons.refresh} size={16} color="#475569" />
                        </button>
                    </div>
                </div>

                {/* Quotations Table */}
                <div className="op-table-responsive">
                    <table className="op-modern-table">
                        <thead>
                            <tr>
                                <th style={{ width: 140 }}>Quotation #</th>
                                <th>Customer</th>
                                <th>Subject</th>
                                <th style={{ width: 140 }}>Dates</th>
                                <th style={{ width: 130 }}>Total Amount</th>
                                <th style={{ width: 110 }}>Status</th>
                                <th style={{ width: 110, textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="table-empty-cell">
                                        <div className="loading-spinner-wrap">
                                            <div className="loading-spinner" />
                                            <span>Loading quotations...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : paginatedData.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="table-empty-cell">
                                        <div className="empty-state-wrap">
                                            <div className="empty-icon-circle">
                                                <Icon d={icons.file} size={28} color="#94a3b8" />
                                            </div>
                                            <h3>No quotations found</h3>
                                            <p>{searchTerm || statusFilter || clientFilter ? 'Try clearing your filters or search terms.' : 'Create your first price quotation to get started.'}</p>
                                            {!searchTerm && !statusFilter && !clientFilter && (
                                                <button className="op-btn-primary" style={{ marginTop: 14 }} onClick={() => handleOpenModal()}>
                                                    <Icon d={icons.plus} size={16} color="#fff" />
                                                    <span>Create Quotation</span>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                paginatedData.map(q => {
                                    const stKey = (q.status || 'draft').toLowerCase();
                                    const stCfg = STATUS_CONFIG[stKey] || STATUS_CONFIG.draft;
                                    const clientName = q.clientId?.companyName || 'Direct Client';
                                    const clientInitial = clientName.charAt(0).toUpperCase();

                                    return (
                                        <tr key={q._id}>
                                            {/* Quotation Number */}
                                            <td>
                                                <div className="qtn-number-badge" onClick={() => handleViewQuotation(q)} title="View quotation preview">
                                                    <Icon d={icons.file} size={14} color="#16539C" />
                                                    <span>{q.quotationNumber || 'QTN-0000'}</span>
                                                </div>
                                            </td>

                                            {/* Customer */}
                                            <td>
                                                <div className="client-cell">
                                                    <div className="client-avatar">{clientInitial}</div>
                                                    <div className="client-meta">
                                                        <span className="client-name">{clientName}</span>
                                                        {q.clientId?.email && <span className="client-sub">{q.clientId.email}</span>}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Subject */}
                                            <td>
                                                <div className="subject-cell">
                                                    <span className="subject-title">{q.subject || 'Transportation Proposal'}</span>
                                                    {q.routes?.length > 0 && (
                                                        <span className="subject-sub">
                                                            {q.routes.length} route{q.routes.length !== 1 ? 's' : ''} included
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Dates */}
                                            <td>
                                                <div className="dates-cell">
                                                    <span className="date-issued">
                                                        {q.issueDate ? new Date(q.issueDate).toLocaleDateString('en-GB') : '—'}
                                                    </span>
                                                    {q.validUntil && (
                                                        <span className="date-valid">
                                                            Valid till {new Date(q.validUntil).toLocaleDateString('en-GB')}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Amount */}
                                            <td>
                                                <div className="amount-cell">
                                                    <span className="amount-main">{formatCurrency(q.totalAmount || 0, country)}</span>
                                                    {q.vatAmount > 0 && (
                                                        <span className="amount-sub">Incl. VAT</span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Status Badge */}
                                            <td>
                                                <span
                                                    className="status-pill"
                                                    style={{
                                                        backgroundColor: stCfg.bg,
                                                        color: stCfg.color,
                                                        borderColor: stCfg.border
                                                    }}
                                                >
                                                    <span className="status-dot" style={{ backgroundColor: stCfg.dot }} />
                                                    {stCfg.label}
                                                </span>
                                            </td>

                                            {/* Actions */}
                                            <td>
                                                <div className="row-actions">
                                                    <button
                                                        className="action-icon-btn btn-view"
                                                        onClick={() => handleViewQuotation(q)}
                                                        title="Preview & Print"
                                                    >
                                                        <Icon d={icons.eye} size={15} color="#475569" />
                                                    </button>
                                                    <button
                                                        className="action-icon-btn btn-edit"
                                                        onClick={() => handleOpenModal(q)}
                                                        title="Edit Quotation"
                                                    >
                                                        <Icon d={icons.edit} size={15} color="#16539C" />
                                                    </button>
                                                    <button
                                                        className="action-icon-btn btn-delete"
                                                        onClick={() => handleDelete(q._id, q.quotationNumber)}
                                                        title="Delete Quotation"
                                                    >
                                                        <Icon d={icons.trash} size={15} color="#dc2626" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Table Footer with Pagination */}
                {!loading && filtered.length > 0 && (
                    <div className="op-table-footer">
                        <div className="pagination-info">
                            Showing <strong>{(page - 1) * pageSize + 1}</strong> to <strong>{Math.min(filtered.length, page * pageSize)}</strong> of <strong>{filtered.length}</strong> quotations
                        </div>
                        <div className="pagination-controls">
                            <button
                                className="pagination-btn"
                                disabled={page === 1}
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    className={`pagination-number ${page === p ? 'active' : ''}`}
                                    onClick={() => setPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                className="pagination-btn"
                                disabled={page === totalPages}
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Quick View / Print Modal */}
            {viewModalOpen && selectedQuotation && (
                <div className="op-modal-overlay" onClick={() => setViewModalOpen(false)}>
                    <div className="op-modal-dialog" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-header-title">
                                <h2>Quotation Preview</h2>
                                <span className="modal-ref">{selectedQuotation.quotationNumber || 'QTN-XXXX'}</span>
                            </div>
                            <div className="modal-header-actions">
                                <button className="op-btn-secondary" onClick={handlePrint}>
                                    <Icon d={icons.printer} size={15} color="#475569" />
                                    <span>Print</span>
                                </button>
                                <button className="modal-close-btn" onClick={() => setViewModalOpen(false)}>
                                    <Icon d={icons.x} size={18} color="#64748b" />
                                </button>
                            </div>
                        </div>

                        <div className="modal-body-scroll">
                            <div id="printable-quotation" className="quotation-print-paper">
                                {/* Print Paper Header */}
                                <div className="print-header-grid">
                                    <div>
                                        {operatorDetails?.logo ? (
                                            <img
                                                src={`${UPLOAD_URL}${operatorDetails.logo}`}
                                                alt="Company Logo"
                                                style={{ maxHeight: 50, maxWidth: 180, marginBottom: 8, objectFit: 'contain' }}
                                            />
                                        ) : (
                                            <h1 style={{ margin: '0 0 6px', fontSize: 24, fontWeight: 900, color: '#063A5C' }}>
                                                {operatorDetails?.companyName || 'OPSMATICZ'}
                                            </h1>
                                        )}
                                        <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                                            <div>{operatorDetails?.address || 'Fleet Operations Hub'}</div>
                                            <div>{operatorDetails?.email || ''} {operatorDetails?.phone ? `• ${operatorDetails.phone}` : ''}</div>
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: 22, fontWeight: 900, color: '#063A5C', letterSpacing: '-0.5px' }}>
                                            PROPOSAL / QUOTATION
                                        </div>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#16539C', marginTop: 4 }}>
                                            {selectedQuotation.quotationNumber}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                                            <div>Issued: {selectedQuotation.issueDate ? new Date(selectedQuotation.issueDate).toLocaleDateString('en-GB') : '—'}</div>
                                            <div>Valid Until: {selectedQuotation.validUntil ? new Date(selectedQuotation.validUntil).toLocaleDateString('en-GB') : '—'}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="print-divider" />

                                {/* Client & Overview */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
                                    <div style={{ background: '#f8fafc', padding: 16, borderRadius: 10, border: '1px solid #e2e8f0' }}>
                                        <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>
                                            PREPARED FOR
                                        </div>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                                            {selectedQuotation.clientId?.companyName || 'Client'}
                                        </div>
                                        {selectedQuotation.clientId?.contactPerson && (
                                            <div style={{ fontSize: 12, color: '#475569', marginTop: 3 }}>
                                                Attn: {selectedQuotation.clientId.contactPerson}
                                            </div>
                                        )}
                                        {selectedQuotation.clientId?.email && (
                                            <div style={{ fontSize: 12, color: '#64748b' }}>
                                                {selectedQuotation.clientId.email}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ background: '#f8fafc', padding: 16, borderRadius: 10, border: '1px solid #e2e8f0' }}>
                                        <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>
                                            PROPOSAL SUMMARY
                                        </div>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                                            {selectedQuotation.subject || 'Transportation Service Agreement'}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#475569', marginTop: 3 }}>
                                            Status: <strong style={{ textTransform: 'uppercase', color: STATUS_CONFIG[(selectedQuotation.status || 'draft').toLowerCase()]?.color || '#000' }}>{selectedQuotation.status || 'Draft'}</strong>
                                        </div>
                                    </div>
                                </div>

                                {/* Routes Table */}
                                {selectedQuotation.routes?.length > 0 && (
                                    <div style={{ marginBottom: 24 }}>
                                        <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            Service Route Specifications
                                        </div>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                            <thead>
                                                <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                                                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Route / Description</th>
                                                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#475569' }}>Vehicle Type</th>
                                                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#475569' }}>Trips / Day</th>
                                                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#475569' }}>Rate / Trip</th>
                                                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#475569' }}>Monthly Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedQuotation.routes.map((r, idx) => (
                                                    <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                        <td style={{ padding: '12px', fontWeight: 600, color: '#1e293b' }}>
                                                            {r.routeName || r.routeId?.name || `Route ${idx + 1}`}
                                                        </td>
                                                        <td style={{ padding: '12px', textAlign: 'center', color: '#475569' }}>
                                                            {r.vehicleType || 'Standard'}
                                                        </td>
                                                        <td style={{ padding: '12px', textAlign: 'center', color: '#475569' }}>
                                                            {r.tripsDaily || r.tripsMonthly || 1}
                                                        </td>
                                                        <td style={{ padding: '12px', textAlign: 'right', color: '#475569' }}>
                                                            {formatCurrency(r.ratePerTrip || r.amountPerTrip || 0, country)}
                                                        </td>
                                                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>
                                                            {formatCurrency(r.totalAmount || r.monthlyRate || 0, country)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Financial Summary */}
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                                    <div style={{ width: 280, background: '#f8fafc', padding: 18, borderRadius: 12, border: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#64748b', marginBottom: 8 }}>
                                            <span>Subtotal:</span>
                                            <span style={{ fontWeight: 600, color: '#1e293b' }}>
                                                {formatCurrency(selectedQuotation.subtotal || selectedQuotation.totalAmount || 0, country)}
                                            </span>
                                        </div>
                                        {selectedQuotation.vatAmount > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#64748b', marginBottom: 8 }}>
                                                <span>VAT ({selectedQuotation.vatPercentage || 5}%):</span>
                                                <span style={{ fontWeight: 600, color: '#1e293b' }}>
                                                    {formatCurrency(selectedQuotation.vatAmount, country)}
                                                </span>
                                            </div>
                                        )}
                                        <div style={{ height: 1, background: '#cbd5e1', margin: '10px 0' }} />
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 800, color: '#063A5C' }}>
                                            <span>Total:</span>
                                            <span>{formatCurrency(selectedQuotation.totalAmount || 0, country)}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Terms & Conditions */}
                                {selectedQuotation.terms && (
                                    <div style={{ marginTop: 24, padding: 14, background: '#fdfdfe', borderRadius: 8, border: '1px dashed #cbd5e1', fontSize: 11, color: '#64748b', lineHeight: 1.5 }}>
                                        <div style={{ fontWeight: 700, color: '#475569', marginBottom: 4 }}>Terms & Conditions:</div>
                                        <div dangerouslySetInnerHTML={{ __html: selectedQuotation.terms }} />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button className="op-btn-secondary" onClick={() => setViewModalOpen(false)}>
                                Close
                            </button>
                            <button
                                className="op-btn-primary"
                                onClick={() => {
                                    setViewModalOpen(false);
                                    handleOpenModal(selectedQuotation);
                                }}
                            >
                                <Icon d={icons.edit} size={15} color="#fff" />
                                <span>Edit This Quotation</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
